import { useCallback, useEffect } from 'react';
import { ThreeEvent, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useSketchStore } from '../state/useSketchStore';
import { useAppStore } from '../state/store';
import { SketchSolidLayer } from './sketch/SketchSolidLayer';
import { SketchLooseEdgeLayer } from './sketch/SketchLooseEdgeLayer';
import { SketchSolidEdgePickLayer } from './sketch/SketchSolidEdgePickLayer';
import { SketchSolidVertexPickLayer } from './sketch/SketchSolidVertexPickLayer';
import { SketchLooseVertexPickLayer } from './sketch/SketchLooseVertexPickLayer';
import { SketchSunLight } from './sketch/SketchSunLight';
import { SketchSky } from './sketch/SketchSky';
import { SketchGuideLayer } from './sketch/SketchGuideLayer';
import { SketchSectionPlaneMarker } from './sketch/SketchSectionPlaneMarker';
import { SketchAnnotationLayer } from './sketch/SketchAnnotationLayer';
import { emitSketchDown, emitSketchMove } from '../tools/sketch/sketchToolBus';
import { SketchToolScope } from '../tools/sketch/SketchToolScope';
import { SketchInvalidateBridge } from '../tools/sketch/SketchCameraTools';
// Cross-workbench content (B4 edit bridge): CAD entities and BIM elements
// drawn elsewhere are visible/selectable here — through the SAME shaded
// renderers those workbenches use (EntityLayer, BIMElementLayer), so every
// workbench shows one synchronized shaded model. Sketch solids mirrored in
// the ledger stay live in the other direction.
import { EntityLayer } from './EntityLayer';
import { BIMShadedLayer } from './BIMShadedLayer';
import { WallHoverControlLayer } from './WallHoverControlLayer';
import { sketchStyleToViewMode } from './sketch/faceMaterial';
import { GumballLayer } from './GumballLayer';
import { setEntityClickRouter } from './toolEventBus';

/**
 * Foreign-reference selection router for the Sketch viewport. The shared
 * SceneContent router does not exist here (different scene tree), so the
 * cross-workbench reference layers register this one instead: clicking a
 * foreign reference routes into the shared app selection space (Shift =
 * add, Ctrl = remove), which is what ERASE / PropertiesPanel / Gumball
 * read — regardless of which workbench the object came from.
 *
 * Gated to the select tool: while a sketch modify tool owns the pointer
 * (MOVE / ERASE / ...), a foreign click must never rewrite the selection
 * the tool is about to act on — the tools themselves resolve foreign
 * targets through the edit router.
 */
function useForeignSelectionRouter(): void {
    useEffect(() => {
        setEntityClickRouter((entityId, event) => {
            if (useAppStore.getState().activeTool !== 'select') return;
            const app = useAppStore.getState();
            if (event.shiftKey) {
                app.addToSelection([entityId]);
            } else if (event.ctrlKey) {
                app.removeFromSelection([entityId]);
            } else {
                app.select([entityId]);
            }
        });
        return () => setEntityClickRouter(null);
    }, []);
}

/** Section plane / clipping box → global clipping planes. */
function SketchSectionClipping() {
    const sectionPlane = useSketchStore((s) => s.sectionPlane);
    const sectionBox = useSketchStore((s) => s.sectionBox);
    const gl = useThree((s) => s.gl);
    useEffect(() => {
        if (sectionBox) {
            // Inward-facing planes on all six sides: only geometry inside the
            // box renders (Sketch's Clipping Box behavior).
            const [minX, minY, minZ] = sectionBox.min;
            const [maxX, maxY, maxZ] = sectionBox.max;
            gl.clippingPlanes = [
                new THREE.Plane(new THREE.Vector3(1, 0, 0), -minX),
                new THREE.Plane(new THREE.Vector3(-1, 0, 0), maxX),
                new THREE.Plane(new THREE.Vector3(0, 1, 0), -minY),
                new THREE.Plane(new THREE.Vector3(0, -1, 0), maxY),
                new THREE.Plane(new THREE.Vector3(0, 0, 1), -minZ),
                new THREE.Plane(new THREE.Vector3(0, 0, -1), maxZ),
            ];
            gl.localClippingEnabled = true;
            return;
        }
        if (!sectionPlane) {
            gl.clippingPlanes = [];
            gl.localClippingEnabled = false;
            return;
        }
        const plane = new THREE.Plane(
            new THREE.Vector3(...sectionPlane.normal).normalize(),
            sectionPlane.constant
        );
        gl.clippingPlanes = [plane];
        gl.localClippingEnabled = true;
        return () => {
            gl.clippingPlanes = [];
            gl.localClippingEnabled = false;
        };
    }, [sectionPlane, sectionBox, gl]);
    return null;
}

/**
 * Sketch scene content — freeform modeling scene.
 *
 * Layers: solids (face-per-mesh, interactive) → guides (dashed aids) → the
 * invisible event-catching plane. Face meshes stopPropagation their events
 * and route through the tool bus; the plane only ever sees "empty space"
 * hits and emits the plane route (default: clear selection — the Sketch
 * click contract).
 */
export function SketchSceneContent() {
    const shadowsEnabled = useSketchStore((s) => s.shadows.enabled);
    const lightLevel = useSketchStore((s) => s.shadows.light);
    // The sketch display style shades the foreign content too (BIM elements
    // render shaded/wireframe/hidden-line exactly like the sketch solids).
    const displayStyle = useSketchStore((s) => s.displayStyle);
    useForeignSelectionRouter();

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (event.button !== 0) return;
        emitSketchDown({
            kind: 'plane',
            point: event.point.clone(),
            shiftKey: event.nativeEvent.shiftKey,
            ctrlKey: event.nativeEvent.ctrlKey,
            altKey: event.nativeEvent.altKey,
            button: event.button,
            pointerPx: { x: event.nativeEvent.clientX, y: event.nativeEvent.clientY },
        });
    }, []);

    const handlePointerMove = useCallback((event: ThreeEvent<PointerEvent>) => {
        emitSketchMove({
            kind: 'plane',
            point: event.point.clone(),
            shiftKey: event.nativeEvent.shiftKey,
            ctrlKey: event.nativeEvent.ctrlKey,
            altKey: event.nativeEvent.altKey,
            button: -1,
            pointerPx: { x: event.nativeEvent.clientX, y: event.nativeEvent.clientY },
        });
    }, []);

    return (
        <group>
            {/* SketchUp-style sky gradient background (Settings › Sketch):
                painted first, everything composes over it. */}
            <SketchSky />
            {/* Base fill, ALWAYS on (the Shadows panel only adds the sun):
                the viewport's lighting slot is null and the procedural IBL
                alone gives vertical faces nothing, so away-from-light
                surfaces collapsed to pitch black. Ambient + hemisphere keep
                every face at a readable dark tone of its material; the
                intensity tracks the Light slider for one graded rig. */}
            <ambientLight intensity={0.55 + lightLevel * 0.25} />
            <hemisphereLight args={['#cfd8e0', '#8a7f70', 0.35]} />
            {shadowsEnabled && <SketchSunLight />}
            <SketchSectionClipping />
            {/* Manual camera tools (Zoom Extents / Walk / ...) schedule
                demand-render frames through this bridge. */}
            <SketchInvalidateBridge />
            <SketchSectionPlaneMarker />
            <SketchAnnotationLayer />
            {/* Cross-workbench references mount BELOW the sketch layers so
                the freeform tools always win the pointer over their own
                faces; foreign references are pointer-transparent only in
                the sense that they never block sketch picking. */}
            <EntityLayer showGroundPlane={false} />
            <BIMShadedLayer viewMode={sketchStyleToViewMode(displayStyle)} />
            <GumballLayer />
            <SketchToolScope />
            <SketchSolidLayer />
            <SketchSolidEdgePickLayer />
            {/* Vertex picks mount AFTER the edge picks: at a corner the
                sphere wins the pointer over the incident edge cylinders
                (SketchUp endpoint precedence). */}
            <SketchSolidVertexPickLayer />
            <SketchLooseEdgeLayer />
            {/* Loose endpoint picks mount LAST among the geometry picks: a
                drawn segment's start/end wins the pointer over its own edge
                cylinder and over a coincident solid corner (SketchUp
                endpoint precedence on the line tools). */}
            <SketchLooseVertexPickLayer />
            <SketchGuideLayer />
            {/* BIM wall hover drag control (extend from ends / junctions):
                the same selection-free affordance the BIM workbench has;
                walls render here through the shared BIMShadedLayer. */}
            <WallHoverControlLayer />

            {/* Event Catching Plane: invisible, world-scale, at Z=0. Rendered
                LAST so face meshes handle their own hits first. */}
            <mesh
                visible={false}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                userData={{ isEventPlane: true }}
            >
                <planeGeometry args={[10000, 10000]} />
            </mesh>
        </group>
    );
}

